/**
 * The IndexedDB save adapter (R10, architecture.md §12) — WHERE snapshots
 * live in the browser.
 *
 * @remarks
 * One database, one object store, one record per game id: autosave replaces
 * rather than accumulates, because the AUTOMATIC ruling means the latest
 * committed state IS the save — slots, if they ever ship, are flavor on top
 * of this same format, not a different mechanism.
 *
 * Deliberately dependency-free and promise-wrapped by hand: the IndexedDB
 * request dance is a page of code, and a wrapper library would be the first
 * runtime dependency this app has that the engine did not force.
 *
 * Failure posture matches the listener's: a storage error loses one write or
 * one resume, never a session. `readSnapshot` resolves `undefined` on any
 * failure — the boot path treats that exactly like "no save yet".
 *
 * @packageDocumentation
 */

import type { SaveSnapshot } from '@orpheus/content-schema';
import type { SaveStorageAdapter } from '@orpheus/player-host';

const DATABASE_NAME = 'orpheus-saves';
const STORE_NAME = 'snapshots';
const DATABASE_VERSION = 1;

/** Open (and on first run, shape) the saves database. */
const openSavesDatabase = async (): Promise<IDBDatabase> =>
	new Promise((resolve, reject) => {
		const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);

		request.onupgradeneeded = (): void => {
			request.result.createObjectStore(STORE_NAME);
		};
		request.onsuccess = (): void => {
			resolve(request.result);
		};
		request.onerror = (): void => {
			reject(request.error ?? new Error('IndexedDB open failed'));
		};
	});

/** Run one transaction against the snapshots store and settle with its result. */
const withSnapshotStore = async <Result>(
	mode: IDBTransactionMode,
	run: (store: IDBObjectStore) => IDBRequest<Result>,
): Promise<Result> => {
	const database = await openSavesDatabase();

	try {
		return await new Promise<Result>((resolve, reject) => {
			const request = run(database.transaction(STORE_NAME, mode).objectStore(STORE_NAME));

			request.onsuccess = (): void => {
				resolve(request.result);
			};
			request.onerror = (): void => {
				reject(request.error ?? new Error('IndexedDB request failed'));
			};
		});
	} finally {
		database.close();
	}
};

/** The browser's save storage — see the module remarks for the failure posture. */
export const createIndexedDbSaveStorage = (): SaveStorageAdapter => ({
	async readSnapshot(bundleId: string): Promise<unknown> {
		try {
			return await withSnapshotStore('readonly', (store) => store.get(bundleId));
		} catch {
			return undefined;
		}
	},

	async writeSnapshot(snapshot: SaveSnapshot): Promise<void> {
		await withSnapshotStore('readwrite', (store) => store.put(snapshot, snapshot.bundleId));
	},
});
